import { useNavigate } from "react-router-dom";
import { useTranslations } from "@/lib/useTranslations";
import { useEffect, useState } from "react";

import { Button } from "@/components/ui/Button";
import { DateRangePicker } from "@/components/ui/DateRangePicker";

import type { LibraryPageSize } from "@/core/services/types/library-pagination.types";
import type {
  EventLibraryCategoryCounts,
  EventLibraryCategoryFilter,
  EventLibraryDateFilters,
  EventLibraryTab,
  EventLibrarySortState,
} from "@/core/services/types/event.types";
import {
  EVENT_LIBRARY_CATEGORY_FILTERS,
  EMPTY_EVENT_LIBRARY_DATE_FILTERS,
  buildEventLibraryHref,
  countActiveEventLibraryFilters,
} from "@/core/services/types/event.types";

const selectClassName =
  "w-full wireframe-border bg-whart-bg px-3 py-2 text-sm text-whart-text outline-none focus:border-whart-dark";

interface EventsLibraryFiltersPanelProps {
  tab: EventLibraryTab;
  category: EventLibraryCategoryFilter;
  categoryCounts: EventLibraryCategoryCounts;
  dateFilters: EventLibraryDateFilters;
  searchQuery: string;
  sort: EventLibrarySortState;
  pageSize: LibraryPageSize;
}

function ChevronIcon({ expanded }: { expanded: boolean }) {
  return (
    <svg
      aria-hidden
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      className={`size-4 text-neutral-500 transition-transform ${expanded ? "rotate-180" : ""}`}
    >
      <path d="M4 6l4 4 4-4" />
    </svg>
  );
}

export function EventsLibraryFiltersPanel({
  tab,
  category,
  categoryCounts,
  dateFilters,
  searchQuery,
  sort,
  pageSize,
}: EventsLibraryFiltersPanelProps) {
  const t = useTranslations("events.library");
  const navigate = useNavigate();
  const activeFilterCount = countActiveEventLibraryFilters(category, dateFilters);
  const hasActiveFilters = activeFilterCount > 0;
  const [isOpen, setIsOpen] = useState(hasActiveFilters);

  useEffect(() => {
    if (hasActiveFilters) {
      setIsOpen(true);
    }
  }, [hasActiveFilters]);

  function navigateWithFilters(next: {
    category?: EventLibraryCategoryFilter;
    dateFilters?: EventLibraryDateFilters;
  }) {
    navigate(
      buildEventLibraryHref({
        tab,
        category: next.category ?? category,
        dateFilters: next.dateFilters ?? dateFilters,
        page: 1,
        searchQuery,
        sort,
        pageSize,
      }),
    );
  }

  function handleCategoryChange(value: string) {
    navigateWithFilters({
      category: value as EventLibraryCategoryFilter,
    });
  }

  function handleStartDateApply(from: string | null, to: string | null) {
    navigateWithFilters({
      dateFilters: {
        ...dateFilters,
        startFrom: from,
        startTo: to,
      },
    });
  }

  function handleEndDateApply(from: string | null, to: string | null) {
    navigateWithFilters({
      dateFilters: {
        ...dateFilters,
        endFrom: from,
        endTo: to,
      },
    });
  }

  function handleResetFilters() {
    navigate(
      buildEventLibraryHref({
        tab,
        page: 1,
        searchQuery,
        category: "ALL",
        dateFilters: EMPTY_EVENT_LIBRARY_DATE_FILTERS,
        sort,
        pageSize,
      }),
    );
  }

  return (
    <section className="overflow-hidden rounded-xl wireframe-border">
      <button
        type="button"
        onClick={() => setIsOpen((current) => !current)}
        aria-expanded={isOpen}
        aria-controls="events-library-filters"
        className="flex w-full items-center justify-between gap-3 bg-whart-bg px-4 py-3 text-left text-sm font-medium text-whart-text transition-colors hover:bg-neutral-50"
      >
        <span className="inline-flex items-center gap-2">
          {t("filtersPanelTitle")}
          {hasActiveFilters ? (
            <span className="inline-flex rounded-full bg-yellow-100 px-2 py-0.5 text-xs font-medium text-zinc-900">
              {t("filtersActiveCount", { count: activeFilterCount })}
            </span>
          ) : null}
        </span>
        <span className="inline-flex items-center gap-2">
          {hasActiveFilters ? (
            <Button
              type="button"
              variant="secondary"
              className="px-3 py-1 text-xs"
              onClick={(event) => {
                event.stopPropagation();
                handleResetFilters();
              }}
            >
              {t("filtersClear")}
            </Button>
          ) : null}
          <ChevronIcon expanded={isOpen} />
        </span>
      </button>

      {isOpen ? (
        <div
          id="events-library-filters"
          className="space-y-4 border-t border-black/10 px-4 py-4"
        >
          <div className="space-y-1.5">
            <label
              htmlFor="events-library-category"
              className="text-xs font-medium uppercase tracking-wider text-neutral-500"
            >
              {t("filtersCategoryLabel")}
            </label>
            <select
              id="events-library-category"
              value={category}
              onChange={(event) => handleCategoryChange(event.target.value)}
              className={selectClassName}
            >
              {EVENT_LIBRARY_CATEGORY_FILTERS.map((categoryFilter) => (
                <option key={categoryFilter} value={categoryFilter}>
                  {t(`categoryFilters.${categoryFilter}`)} (
                  {categoryCounts[categoryFilter]})
                </option>
              ))}
            </select>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <DateRangePicker
              id="events-library-start-date"
              label={t("filtersStartDateLabel")}
              from={dateFilters.startFrom}
              to={dateFilters.startTo}
              allDatesLabel={t("filtersDateAllLabel")}
              onApply={handleStartDateApply}
            />

            <DateRangePicker
              id="events-library-end-date"
              label={t("filtersEndDateLabel")}
              from={dateFilters.endFrom}
              to={dateFilters.endTo}
              allDatesLabel={t("filtersDateAllLabel")}
              onApply={handleEndDateApply}
            />
          </div>
        </div>
      ) : null}
    </section>
  );
}
